import { useQuery } from "@tanstack/react-query";
import { priorityService } from "@/services/priorityService";
import type { Priority } from "@/types";

/**
 * The server's copy of a deliverable, seeded with the row the caller already has.
 *
 * Callers open the edit dialog from a list and hold whatever that list gave them — a snapshot that
 * goes stale the moment the row changes underneath it, e.g. when "Claim as DRI" adds a DRI. Seeding
 * the query instead of replacing the caller's row means the dialog always has something to render:
 * no create-form flash on open, and no reset to `null` when the row leaves the list mid-edit.
 */
export function useLivePriority(seed: Priority | null | undefined): Priority | null {
  const pid = seed?.pid;
  const { data } = useQuery({
    queryKey: ["priorities", pid],
    queryFn: () => priorityService.getPriority(pid as string),
    enabled: Boolean(pid),
    // Not `initialData`: that would be written to the cache and count as fresh, so the stale snapshot
    // would be served to every other reader of this key.
    placeholderData: seed ?? undefined,
  });
  if (!pid) return null;
  return data ?? seed ?? null;
}
